import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { prop } from 'ramda';

import { SvgIcon } from '@interlayer/js/app/components/common/SvgIcon';
import { Portal } from '@interlayer/js/app/components/system/Portal';
import { PortalPlace } from '@interlayer/js/app/components/system/Portal/components/PortalPlace';
import { Button } from '@interlayer/js/components/common/Button';
import { Link } from '@interlayer/js/components/common/Link';
import { RecentlyBlock } from './components/RecentlyBlock';
import { Timer } from './components/Timer';

import { useOpenChat } from '@core/js/components/system/CommunicationWidgets/hooks';
import { useTranslations } from '@core/js/hooks/i18n';
import { componentService } from '@core/js/services/component.service';
import { useAppSelector, usePageSelector, useUserSelector } from '@core/js/store/hooks';

import { BUTTON_IDS } from '@interlayer/js/app/components/main/PlayBingoNowButton/constants';
import { BOTTOM_STICKY_IDS } from '@interlayer/js/app/components/system/BottomStickyElements/constants';
import { useInDesktop } from '@interlayer/js/app/hooks/device/hooks/useMediaQuery';
import { useConfigTag } from '@interlayer/js/app/hooks/useConfigTag';
import { useDynamicTags } from '@interlayer/js/app/hooks/useDynamicTags';
import { useIsVisible } from '@interlayer/js/app/hooks/useIsVisible';
import { clsx } from '@interlayer/js/app/utils/clsx.util';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/app/utils/constants';
import { useIsShowChat } from '@interlayer/js/components/system/CommunicationWidgets/buzz/hooks/useIsShowChat';
import { setIsStickyFooter } from '@interlayer/js/components/system/Page/store/buzz/actions';
import { CONFIG_TAG_TYPE } from '@interlayer/js/components/system/TagManager/buzz/constants';
import { TFooterLinkConfigTag } from '@interlayer/js/components/system/TagManager/buzz/types';

import { TStickyFooterProps } from './types';

import style from './StickyFooter.scss';

export const StickyFooter: React.FC<TStickyFooterProps> = ({ withrecently, withbands, withjackpots, withInfo = true }) => {
    const recentlyBlockEl = useRef<HTMLDivElement>(null);
    const openChat = useOpenChat();
    const dispatch = useDispatch();
    const isShowChat = useIsShowChat();
    const isDesktop = useInDesktop();
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const translate = useTranslations('playtech.mobile.system.stickyFooter.');
    const showMobileMenu = useAppSelector(prop('showMobileMenu'));
    const isVisible = useIsVisible(document.querySelector('footer'));
    const isStickyFooter = usePageSelector(prop('isStickyFooter'));
    const { config } = useConfigTag(CONFIG_TAG_TYPE.footerLinkConfig) as TFooterLinkConfigTag || {};
    const responsibleLink = useMemo(() => {
        if (!config) {
            return '';
        }

        return isLoggedIn ? config.loggedInLink : config.loggedOutLink || '';
    }, [config, isLoggedIn]);

    useEffect(() => {
        dispatch(setIsStickyFooter(!isVisible));
    }, [dispatch, isVisible]);

    const [
        dTagHelpLink = '/help',
        dTagOver18PageLink = '/18plus'
    ] = useDynamicTags([
        DYNAMIC_TAG_NAMES.stickyFooterHelpPageLink,
        DYNAMIC_TAG_NAMES.stickyFooterOver18PageLink
    ]);

    const isOpenMenu = isDesktop && showMobileMenu && isStickyFooter;

    const footer = useMemo(() => (
        <div className={style.stickyWrapper}>
            <div className={clsx(style.stickyFooter, { [style.sticked]: isStickyFooter })}>
                <div
                    className={clsx(style.container, {
                        [style.containerSticky]: isOpenMenu,
                        [style.isStatic]: !isDesktop && !isStickyFooter
                    })}
                >
                    <div className={clsx(style.col, style.over18)}>
                        <Link className={style.over18Link} href={dTagOver18PageLink}>
                            <SvgIcon className={style.over18Icon} type="over-eighteen" />
                        </Link>
                    </div>

                    <div className={clsx(style.col, style.responsibleGaming)}>
                        <Link
                            href={responsibleLink}
                            className={style.responsibleGamingLink}
                        >
                            <SvgIcon type="responsible-gaming" className={style.responsibleIcon} />
                        </Link>
                    </div>

                    <div className={clsx(style.col, style.timerWrapper)}>
                        <Timer />
                    </div>

                    {isShowChat && (
                        <Button
                            onClick={openChat}
                            className={style.helpButton}
                            view="secondary"
                            size="small"
                        >
                            {translate('live-chat')}
                        </Button>
                    )}

                    <Button
                        href={dTagHelpLink}
                        className={clsx(style.helpButton, { [style.helpButtonWithChat]: isShowChat })}
                        size="small"
                    >
                        {translate('help')}
                    </Button>

                    {isLoggedIn && withrecently && (
                        <RecentlyBlock
                            isFixed={!isStickyFooter}
                            withBands={withbands}
                            withJackpots={withjackpots}
                            withInfo={withInfo}
                            refEl={recentlyBlockEl}
                        />
                    )}
                </div>
            </div>
        </div>
    ), [
        dTagHelpLink, dTagOver18PageLink, isLoggedIn, isOpenMenu, isShowChat, responsibleLink,
        isStickyFooter, openChat, translate, withInfo, withbands, withjackpots, withrecently, isDesktop
    ]);

    return (
        <>
            <PortalPlace id={BUTTON_IDS.PLAY_BINGO_NOW_BUTTON_ID_MOBILE} />

            {isStickyFooter
                ? <Portal id={BOTTOM_STICKY_IDS.footer}>{footer}</Portal>
                : footer
            }
        </>
    );
};

componentService.registerWebContentComponents({ StickyFooter });
